import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, LogOut, Shield, BarChart3, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import LanguageSelector from './LanguageSelector';

const Layout = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: t('nav.dashboard'), testId: 'dashboard' },
    { to: '/customers', icon: Users, label: t('nav.customers'), testId: 'customers' },
    { to: '/invoices', icon: FileText, label: t('nav.invoices'), testId: 'invoices' },
    { to: '/cashbook', icon: Wallet, label: t('nav.cashbook'), testId: 'cashbook' },
    { to: '/reports', icon: BarChart3, label: t('nav.reports'), testId: 'reports' },
  ];

  // Add users management for admins only
  if (user?.role === 'admin') {
    navItems.push({ to: '/users', icon: Shield, label: t('nav.users'), testId: 'users' });
  }

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-sm shadow-md border border-slate-200"
        data-testid="mobile-menu-button"
      >
        {mobileMenuOpen ? (
          <svg className="w-6 h-6 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:relative
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        transition-transform duration-300 ease-in-out
        w-64 lg:w-60 h-full bg-white border-r border-slate-200 flex flex-col z-40
      `}>
        <div className="p-6 border-b border-slate-200">
          <img src="/logo.png" alt="Kaufmanns Consulting Logo" className="h-10 sm:h-12 mb-2" />
          <p className="text-xs text-slate-500">Rechnungsverwaltung</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-${item.testId}`}
                className={({ isActive }) => 
                  `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-primary text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        
        {/* Language Selector */}
        <div className="p-4 border-t border-slate-200">
          <LanguageSelector />
        </div>

        <div className="p-4 border-t border-slate-200">
          <div className="mb-3 px-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('nav.user')}</p>
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name || user?.email}</p>
            {user?.role === 'admin' && (
              <Badge className="mt-1 bg-primary text-white rounded-full text-xs">
                {t('users.roleAdmin')}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto w-full lg:w-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;